import {useMemo} from "react";
import {useQuery} from "react-query";
import {useSelector} from "react-redux";
import {App} from "crqlar-react-common/dist/models/app/app.model";
import {OrganizationApp} from "crqlar-react-common/dist/models/app/organizationApp.model";
import {Page} from "crqlar-react-common/dist/models/common/page.model";
import {User} from "crqlar-react-common/dist/models/identity/user.model";
import {Organization} from "crqlar-react-common/dist/models/organization/organization";
import {AppService} from "crqlar-react-common/dist/services/app/app.service";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import {OrganizationService} from "crqlar-react-common/dist/services/identity/organization.service";
import {useOrganizationAppsQuery} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";

export const MY_ACCOUNT_QUERY_KEY = ["myAccount"] as const;
export const MY_ORGANIZATION_QUERY_KEY = ["myOrganization"] as const;
export const MARKETPLACE_APPS_QUERY_KEY = "allMarketplaceApps";

// Identity data only changes through explicit mutations (which must invalidate
// the keys above) or external admin edits. 5 minutes stops every consumer mount
// and window focus from refetching, and keeps the redux-seeded bootstrap data
// (validateToken just fetched it) from being refetched immediately at boot.
const ME_STALE_TIME = 5 * 60_000;

export interface AvailableApp {
    app?: App;
    orgApp: OrganizationApp;
}

export const useMe = () => {
    const authUser = useSelector<any, User | undefined>(state => state.auth.user);
    const authOrganization = useSelector<any, Organization | undefined>(state => state.auth.organization);

    const userQuery = useQuery<User, unknown>(
        MY_ACCOUNT_QUERY_KEY,
        MyAccountService.getMyAccount,
        {
            enabled: !!authUser,
            initialData: authUser,
            staleTime: ME_STALE_TIME,
        }
    );

    const organizationQuery = useQuery<Organization, unknown>(
        MY_ORGANIZATION_QUERY_KEY,
        OrganizationService.getCurrent,
        {
            enabled: !!authUser,
            initialData: authOrganization,
            staleTime: ME_STALE_TIME,
        }
    );

    const organizationAppsQuery = useOrganizationAppsQuery(!!authUser);

    const marketplaceAppsQuery = useQuery<Page<App>, unknown>(
        MARKETPLACE_APPS_QUERY_KEY,
        () => AppService.getAll({
            limit: {offset: 0, limit: 1000, sort: [{field: "order", direction: "DESC"}]},
        }),
        {
            enabled: !!authUser,
            staleTime: ME_STALE_TIME,
        }
    );

    const user = userQuery.data;
    const organization = organizationQuery.data;
    const marketplaceApps = useMemo(
        () => marketplaceAppsQuery.data?.content ?? [],
        [marketplaceAppsQuery.data?.content]
    );
    const organizationApps = useMemo(
        () => organizationAppsQuery.data?.content ?? [],
        [organizationAppsQuery.data?.content]
    );
    const permittedAppIds = user?.permittedAppIds;
    const availableApps = useMemo<AvailableApp[]>(
        () => organizationApps
            .filter(orgApp => (
                permittedAppIds == null
                || permittedAppIds.includes(orgApp.id as string)
            ))
            .map(orgApp => ({
                app: marketplaceApps.find(app => app.id === orgApp.appId),
                orgApp,
            })),
        [marketplaceApps, organizationApps, permittedAppIds]
    );

    // isError only covers a failed bootstrap (no usable data yet) — a failing
    // background refetch does not un-ready a running session. Neither flag
    // being true means the bootstrap is still loading.
    const isReady = !!user
        && !!organization
        && !!organizationAppsQuery.data
        && !!marketplaceAppsQuery.data;
    const isError = !isReady && (
        userQuery.isError
        || organizationQuery.isError
        || organizationAppsQuery.isError
        || marketplaceAppsQuery.isError
    );

    return {
        availableApps,
        isError,
        isReady,
        marketplaceApps,
        organization,
        organizationApps,
        user,
    };
};
